import { useCallback, useEffect, useMemo, useState } from "react";

import useService from "../../../../hooks/useService";
import {
  buildSettlementPaymentTypeDisplayMap,
  mapSettlementPaymentTypeOptions,
} from "../components/payments/helpers/functions";
import { useSettlementStore } from "../store/settlementStore";

import useSettlementPermissions from "./useSettlementPermissions";

export default function useSettlementPaymentsSection() {
  const settlementCheckRepository = useService("repositories/settlement-check-repository");
  const fieldOptionRepository = useService("repositories/field-option-repository");
  const caseDetail = useSettlementStore((state) => state.caseDetail);
  const { canViewPayments, canAddPayments, canEditPayments } = useSettlementPermissions();
  const [payments, setPayments] = useState([]);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState({ mode: null, payment: null });

  const fetchPayments = useCallback(async () => {
    if (!caseDetail?.pk || !canViewPayments) {
      setPayments([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await settlementCheckRepository.query({ case: caseDetail.pk });
      setPayments(response || []);
    } catch (error) {
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  }, [canViewPayments, caseDetail?.pk, settlementCheckRepository]);

  const fetchPaymentTypeOptions = useCallback(async () => {
    if (!canViewPayments) {
      setPaymentTypeOptions([]);
      return;
    }

    try {
      const response = await fieldOptionRepository.query({
        field_name: "check_type",
        ordering: "label",
      });
      setPaymentTypeOptions(mapSettlementPaymentTypeOptions(response || []));
    } catch (error) {
      setPaymentTypeOptions([]);
    }
  }, [canViewPayments, fieldOptionRepository]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    fetchPaymentTypeOptions();
  }, [fetchPaymentTypeOptions]);

  const paymentTypeDisplayMap = useMemo(
    () => buildSettlementPaymentTypeDisplayMap(paymentTypeOptions),
    [paymentTypeOptions]
  );

  return {
    payments,
    paymentTypeOptions,
    paymentTypeDisplayMap,
    isLoading,
    caseDetail,
    canViewPayments,
    canAddPayments,
    canEditPayments,
    modalState,
    fetchPayments,
    handleOpenAddModal: () => setModalState({ mode: "add", payment: null }),
    handleOpenEditModal: (payment) => setModalState({ mode: "edit", payment }),
    handleCloseModal: () => setModalState({ mode: null, payment: null }),
  };
}
